'use memo'
/** @jsxImportSource @emotion/react */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { uiReducer, initialUIState } from './reducer'
import {
  UIContextValue,
  UIActionType,
  HighlightType,
  CLEANUP_INTERVAL_MS,
} from './types'

const UIContext = createContext<UIContextValue | undefined>(undefined)

interface UIProviderProps {
  children: React.ReactNode
}

export const UIProvider: React.FC<UIProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(uiReducer, initialUIState)

  const addHighlightedRow = useCallback(
    (id: string, highlightType: HighlightType = 'success') => {
      dispatch({
        type: UIActionType.ADD_HIGHLIGHTED_ROW,
        payload: { id, highlightType, addedAt: Date.now() },
      })
    },
    []
  )

  const setSelectedOfficial = useCallback((official: any | null) => {
    dispatch({ type: UIActionType.SET_SELECTED_OFFICIAL, payload: official })
  }, [])

  const setOfficialModalOpen = useCallback((open: boolean) => {
    dispatch({ type: UIActionType.SET_OFFICIAL_MODAL_OPEN, payload: open })
  }, [])

  const setAddPlayerSheetOpen = useCallback((open: boolean) => {
    dispatch({ type: UIActionType.SET_ADD_PLAYER_SHEET_OPEN, payload: open })
  }, [])

  useEffect(() => {
    if (state.highlightedRows.size === 0) {
      return
    }
    const interval = setInterval(() => {
      dispatch({
        type: UIActionType.CLEAR_EXPIRED_HIGHLIGHTS,
        payload: Date.now(),
      })
    }, CLEANUP_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [state.highlightedRows])

  const value: UIContextValue = useMemo(
    () => ({
      ...state,
      addHighlightedRow,
      setSelectedOfficial,
      setOfficialModalOpen,
      setAddPlayerSheetOpen,
    }),
    [
      state,
      addHighlightedRow,
      setSelectedOfficial,
      setOfficialModalOpen,
      setAddPlayerSheetOpen,
    ]
  )

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>
}

export const useUIContext = () => {
  const context = useContext(UIContext)
  if (context === undefined) {
    throw new Error('useUIContext must be used within UIProvider')
  }
  return context
}
